import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';

interface NotificationMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationMenu = forwardRef<HTMLDivElement, NotificationMenuProps>(
  ({ isOpen, onClose }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { notifications, loading, error, markAsRead } = useNotifications();

    if (!isOpen) return null;

    // 날짜 포맷팅 함수
    const formatNotificationDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        
        if (diffInSeconds < 60) {
          return '방금 전';
        } else if (diffInSeconds < 3600) {
          const minutes = Math.floor(diffInSeconds / 60);
          return `${minutes}분 전`;
        } else if (diffInSeconds < 86400) {
          const hours = Math.floor(diffInSeconds / 3600);
          return `${hours}시간 전`;
        } else if (diffInSeconds < 2592000) {
          const days = Math.floor(diffInSeconds / 86400);
          return `${days}일 전`;
        } else {
          return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
      } catch (error) {
        return dateString;
      }
    };

    // 알림 클릭 핸들러
    const handleNotificationClick = async (notification: any) => {
      try {
        // 읽음 처리 (항상 읽지 않은 알림들만 표시되므로)
        await markAsRead(notification.notification_id);
      
        // 메뉴 닫기
        onClose();
      
        // 라우팅 처리
        if (notification.route_to) {
          // 경로 정규화: 공백 제거 및 절대/상대 경로 처리
          let rawTarget = String(notification.route_to).trim();
          // /// console.log('notification.route_to', rawTarget, notification);

          if (/^https?:\/\//i.test(rawTarget)) {
            // 외부 절대 URL
            window.open(rawTarget, '_blank');
          } else {
            // 내부 경로: 선행 슬래시 보장하여 절대 경로로 만듦
            let targetPath = rawTarget.startsWith('/') ? rawTarget : `/${rawTarget}`;

            // 지금 http://localhost:4000/community/post/79 에서 버튼을 눌러도 위 notification.route_to에 맞는 주소로 이동했으면 좋겠어 ..
            const currentPath = `${location.pathname}${location.search}${location.hash}`;

            if (currentPath === targetPath) {
              // 동일 경로일 때는 SPA 내 라우팅이 스킵될 수 있으므로 강제 이동
              window.location.assign(targetPath);
            } else if (targetPath.startsWith('/api/')) {
              // API 경로를 커뮤니티 상세 경로로 매핑 (예: /api/posts/79 -> /community/post/79)
              targetPath = targetPath.replace(/^\/api\/(?:post|posts)\//, '/community/post/');
              window.location.assign(targetPath);
            } else {
              navigate(targetPath, { replace: true });
            }
          }
        }
      } catch (error) {
        /// console.error('알림 클릭 처리 실패:', error);
      }
    };

    return (
      <Container ref={ref} data-scroll-bar="false" data-variant="Notification">
        <NotificationContent>
          {loading ? (
            <EmptyStateContainer>
              <EmptyStateText>알림을 불러오는 중...</EmptyStateText>
            </EmptyStateContainer>
          ) : error ? (
            <EmptyStateContainer>
              <EmptyStateText>알림을 불러오는데 실패했습니다.</EmptyStateText>
            </EmptyStateContainer>
          ) : notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <React.Fragment key={notification.notification_id}>
                <NotificationItem onClick={() => handleNotificationClick(notification)}>
                  <NotificationCaption>
                    {notification.notification_type || '알림'}
                  </NotificationCaption>
                  <NotificationTitle>{notification.title}</NotificationTitle>
                  {notification.content && (
                    <NotificationContentText>{notification.content}</NotificationContentText>
                  )}
                  <NotificationDate>
                    {formatNotificationDate(notification.created_at)}
                  </NotificationDate>
                  <UnreadIndicator />
                </NotificationItem>
                {index < notifications.length - 1 && <Separator />}
              </React.Fragment>
            ))
          ) : (
            <EmptyStateContainer>
              <EmptyStateText>새로운 알림이 없습니다.</EmptyStateText>
            </EmptyStateContainer>
          )}
        </NotificationContent>
      </Container>
    );
  }
);

const Container = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 392px;
  max-height: 500px;
  background: var(--Background-Elevated-Normal, white);
  box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  border-radius: 18px;
  outline: 1px rgba(112, 115, 124, 0.08) solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 100;
  margin-top: 8px;
  
  &:before {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    width: 100%;
    height: 8px;
  }
`;

const NotificationContent = styled.div`
  align-self: stretch;
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow-y: auto;
  max-height: 460px;
  
  /* 커스텀 스크롤바 */
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 1000px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.16);
    border-radius: 1000px;
    
    &:hover {
      background: var(--Label-Normal, #171719);
    }
  }
  
  &::-webkit-scrollbar-thumb:active {
    background: var(--Label-Normal, #171719);
  }
`;

const NotificationItem = styled.div`
  width: 392px;
  padding: 12px 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  
  /* 읽지 않은 알림 배경색 */
  /* background-color: rgba(41, 103, 104, 0.02); */
  
  &:hover {
    background-color: rgba(112, 115, 124, 0.04);
  }
`;

const NotificationCaption = styled.div`
  color: var(--Primary-Normal, #448181);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  word-wrap: break-word;
`;

const NotificationTitle = styled.div`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const NotificationContentText = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 19.99px;
  letter-spacing: 0.15px;
  word-wrap: break-word;
`;

const NotificationDate = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  word-wrap: break-word;
`;

const UnreadIndicator = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--Primary-Strong, #296768);
`;

const Separator = styled.div`
  align-self: stretch;
  padding: 8px 32px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  
  &:after {
    content: '';
    align-self: stretch;
    height: 1px;
    background: var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  }
`;

const EmptyStateContainer = styled.div`
  width: 392px;
  padding: 12px 32px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
`;

const EmptyStateText = styled.div`
  align-self: stretch;
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

export default NotificationMenu;
